/**
 * Draws an unread count onto the favicon.
 *
 * The page ships three icon links (a PNG, an SVG and a legacy shortcut icon) and
 * browsers disagree about which one wins — Chrome and Firefox generally prefer the
 * SVG. Overwriting just one of them therefore changes nothing on most browsers, so
 * while a badge is showing this module takes the icon over entirely: it detaches
 * the declared links and installs a single generated one, then puts the originals
 * back when the count returns to zero.
 */
import { formatUnreadCount } from './documentTitle'

const BASE_FAVICON_URL = '/favicon-96x96.png'
const CANVAS_SIZE = 96

/** Badge geometry, as fractions of the canvas — the icon actually ships at 16px. */
const BADGE_RADIUS_RATIO = 0.32
/** Extra width per character once the label outgrows a circle and becomes a pill. */
const BADGE_PILL_BASE_RATIO = 1.1
const BADGE_PILL_PER_CHAR_RATIO = 0.55
const BADGE_FONT_RATIO = 1.35

/**
 * Fixed rather than read from the theme tokens: the favicon renders against the
 * browser's own chrome, which has no relationship to the app's light/dark mode. A
 * token that dims in dark mode would be dimming against the wrong background.
 */
const BADGE_BACKGROUND = '#d32f2f'
const BADGE_TEXT = '#ffffff'

/** The icon links declared in index.html, detached while a badge is displayed. */
let originalIconLinks: HTMLLinkElement[] = []
let managedLink: HTMLLinkElement | null = null
let baseIconPromise: Promise<HTMLImageElement | null> | null = null
let renderedCount = 0

const loadBaseIcon = (): Promise<HTMLImageElement | null> => {
  if (baseIconPromise !== null) return baseIconPromise

  baseIconPromise = new Promise((resolve) => {
    const image = new Image()
    // Same-origin, but the canvas is read back via toDataURL and a tainted canvas
    // would throw there rather than here.
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = BASE_FAVICON_URL
  })

  return baseIconPromise
}

const drawBadgedIcon = (baseIcon: HTMLImageElement, count: number): string | null => {
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_SIZE
  canvas.height = CANVAS_SIZE

  const context = canvas.getContext('2d')
  if (!context) return null

  context.drawImage(baseIcon, 0, 0, CANVAS_SIZE, CANVAS_SIZE)

  const label = formatUnreadCount(count)
  // A single digit sits in a circle; anything wider needs a pill or the glyphs
  // start colliding with the edge at 16px, which is the size that actually ships.
  const radius = CANVAS_SIZE * BADGE_RADIUS_RATIO
  const pillWidth =
    label.length > 1
      ? radius * (BADGE_PILL_BASE_RATIO + BADGE_PILL_PER_CHAR_RATIO * label.length)
      : radius * 2
  const centerY = CANVAS_SIZE - radius
  const left = CANVAS_SIZE - pillWidth
  const right = CANVAS_SIZE

  context.fillStyle = BADGE_BACKGROUND
  context.beginPath()
  context.moveTo(left + radius, centerY - radius)
  context.lineTo(right - radius, centerY - radius)
  context.arcTo(right, centerY - radius, right, centerY, radius)
  context.arcTo(right, centerY + radius, right - radius, centerY + radius, radius)
  context.lineTo(left + radius, centerY + radius)
  context.arcTo(left, centerY + radius, left, centerY, radius)
  context.arcTo(left, centerY - radius, left + radius, centerY - radius, radius)
  context.closePath()
  context.fill()

  context.fillStyle = BADGE_TEXT
  context.font = `bold ${Math.round(radius * BADGE_FONT_RATIO)}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.fillText(label, left + pillWidth / 2, centerY + 1)

  return canvas.toDataURL('image/png')
}

const detachDeclaredIcons = (): void => {
  if (originalIconLinks.length) return
  // Tab icons only. `apple-touch-icon` is deliberately excluded — it is read when
  // someone adds the app to a home screen, not to paint the tab, and detaching it
  // would degrade the installed icon for no benefit.
  originalIconLinks = Array.from(
    document.querySelectorAll<HTMLLinkElement>("link[rel='icon'], link[rel='shortcut icon']"),
  )
  originalIconLinks.forEach((link) => link.remove())
}

const restoreDeclaredIcons = (): void => {
  managedLink?.remove()
  managedLink = null
  originalIconLinks.forEach((link) => document.head.appendChild(link))
  originalIconLinks = []
}

const applyBadgedIcon = (href: string): void => {
  detachDeclaredIcons()
  if (!managedLink) {
    managedLink = document.createElement('link')
    managedLink.rel = 'icon'
    managedLink.type = 'image/png'
    document.head.appendChild(managedLink)
  }
  managedLink.href = href
}

/**
 * Best-effort: a browser that can't give us a 2d context, or a favicon that fails
 * to load, simply keeps the plain icon. The tab title still carries the count.
 */
export const setFaviconBadge = (count: number): void => {
  const next = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
  if (next === renderedCount) return
  renderedCount = next

  if (next === 0) {
    restoreDeclaredIcons()
    return
  }

  void loadBaseIcon().then((baseIcon) => {
    // Re-checked after the await: the count can drop to zero (or change) while the
    // icon is loading, and the stale draw would otherwise win the race.
    if (!baseIcon || renderedCount !== next) return
    const href = drawBadgedIcon(baseIcon, next)
    if (href) applyBadgedIcon(href)
  })
}
